/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { object } from 'prop-types';
import { useDispatch } from 'react-redux';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import ReactHtmlParser from 'react-html-parser';
import { decode as htmlDecode } from '../../../utils/htmlEntity';
import EventDispatcher from '../../../state/reducers/EventDispatcher';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const styles = (theme) => ({
    mainContainer: {
        display: 'grid',
        gridTemplateColumns: '17.3% 1fr',
        columnGap: '1rem',
        padding: '16px 0px',
        borderTop: `1px solid ${(theme.palette.text?.giftListBorder || '#D5D5D5')}`,
        '@media only screen and (max-width: 1024px) ': {
            gridTemplateColumns: '25% 1fr',
        },
        '@media only screen and (max-width: 600.5px) ': {
            position: 'relative',
            paddingBottom: '16px',
            gridTemplateColumns: '30% 1fr',
        },
    },
    productImage: {
        height: '100%',
        '& img': {
            width: '100%',
        },
    },
    productName: {
        fontSize: '1.25rem',
        fontStyle: 'normal',
        fontWeight: '700',
        lineHeight: '28px',
        letterSpacing: '0.2px',
        marginTop: '4px',
        '@media only screen and (max-width: 1023.5px) ': {
            lineHeight: '1.25rem',
            fontSize: '1rem',
        },
        '@media only screen and (max-width: 600px) ': {
            marginTop: '8px',

        },
    },
    productPartNumber: {
        lineHeight: '1.25rem',
        margin: '4px 0px 0px',
        fontSize: '1rem',
        '@media only screen and (max-width: 1023.5px) ': {
            lineHeight: '1.125rem',
        },
        '@media only screen and (max-width: 600px) ': {
            marginTop: '8px',

        },
    },
    button: {
        color: theme.palette.cms?.black || '#2f2f2f ',
        border: `1px solid ${theme.palette.cms?.black || '#2f2f2f'}`,
        marginTop: '13px',
        float: 'left',
        backgroundColor: theme.palette.cms?.white || '#ffffff',
        borderRadius: '3px',
        fontWeight: 700,
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        padding: '7px 16px',
        boxShadow: 'inherit',
        letterSpacing: '0.56px',
        textTransform: 'capitalize',
        minWidth: '160px',
        '&:hover': {
            boxShadow: 'inherit',
            backgroundColor: theme.palette.cms?.white || '#ffffff',
        },
        '@media only screen and (max-width: 600px) ': {
            marginTop: '16px',

        },
        '& .MuiTouchRipple-root span': {
            display: 'none !important',
        },
    },
    crossedPrice: {
        fontWeight: 700,
        textDecoration: 'line-through',
        lineHeight: '1.25rem',
        '@media only screen and (max-width: 600.5px) ': {
            fontSize: '0.875rem',
            lineHeight: '1.125rem',
        },
    },
    discountPrice: {
        color: theme.palette.cms?.errorZipVerifyPDP || '#A1001A',
        paddingLeft: '7px',
        fontWeight: 700,
        lineHeight: '1.25rem',
        '@media only screen and (max-width: 600.5px)': {
            fontSize: '0.875rem ',
            lineHeight: '1.125rem',
        },
        '@media only screen and (max-width: 600px) ': {
            marginTop: '8px',

        },
    },
    price: {
        fontSize: '1rem',
        lineHeight: '1.25rem',
        fontWeight: 700,
        marginTop: '6px',
        '@media only screen and (max-width: 768px) and (min-width:600.5px) ': {
            fontSize: '0.875rem',
            lineHeight: '1.125rem',
        },
    },
    addedToCart: {
        color: theme.palette.cms?.lightGreen || '#3F8D5D',
        fontSize: '1rem',
        fontWeight: 700,
        lineHeight: '1.25rem',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
});

const GiftListAddedProduct = ({
    classes,  recipient,
}) => {
    const {
        name,
        image,
        price,
        discountPrice,
        crossedPrice,
        partNumber,
    } = recipient?.cartDetail?.productDetail;
    const urlDecode = name || '';
    const newName = htmlDecode(urlDecode) || '';
    const dispatch = useDispatch();
    const productPartNumber = partNumber?.includes('-') ? partNumber?.split('-')?.[1] : partNumber;
    return (
        <div className={classes.mainContainer}>
            <div className={classes.productImage}>
                <img src={`${image}?auto=webp`} alt={newName} />
            </div>
            <div className={classes.contentContainer}>
                <div className={classes.addedToCart}>In your cart: </div>
                <div className={classes.productName}>
                    {ReactHtmlParser(newName)}
                </div>
                <p className={classes.productPartNumber}>{productPartNumber}</p>
                <div className={classes.price}>
                    {discountPrice && crossedPrice ? (
                        <>
                            <span className={classes.crossedPrice}>
                                ${crossedPrice}
                            </span>
                            <span className={classes.discountPrice}>
                                ${discountPrice}
                            </span>
                        </>
                    ) : (
                    `$${price}`
                    )}
                </div>
                <Button
                    variant="contained"
                    className={classes.button}
                    onClick={() => {
                        dispatch(EventDispatcher.handleGiftListChooseSomethingDifferent(recipient));
                        dispatch(trackEvent({
                            eventCategory: 'Gift List V2',
                            eventAction: 'Item Removed',
                            eventLabel: productPartNumber || '',
                        }));
                    }}
                >
                    Remove From Cart
                </Button>
            </div>
        </div>
    );
};

GiftListAddedProduct.propTypes = {
    classes: object.isRequired,
    recipient: object.isRequired,
};

export default withStyles(styles)(GiftListAddedProduct);
